import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { Logger } from '../../util/logger';
import { ClosureService } from '../../services/closure.service';

import * as fromClosures from '../actions/closures.actions';
import * as fromReducer from '../reducers/closures.reducer';
import * as fromSelectors from '../selectors/closures.selectors';
import * as fromSearch from '../actions/search.actions';
import * as fromErrors from '../fromErrors.barrel';
import * as fromStore from '../../store';
import { MonitoringService } from '../../services/monitoring.service';
import { FittingsService } from 'src/app/services/fittings.service';

@Injectable()
export class ClosuresEffects {
  constructor(
    private actions$: Actions,
    private closureService: ClosureService,
    private store: Store<fromReducer.State>,
    private logger: Logger,
    private monitoringService: MonitoringService,
    private fittingsService: FittingsService
  ) { }

  loadLookups$ = createEffect(() => this.actions$.pipe(
    ofType(fromClosures.loadClosureLookups),
    withLatestFrom(this.store.select(fromSelectors.selectHasLoadedLookups)),
    filter(([action, alreadyLoaded]) => !!action.force || !alreadyLoaded),
    switchMap(([action, alreadyLoaded]) => this.closureService.getLookups()
      .pipe(map(res => fromClosures.loadClosureLookupsSuccess(res)),
        catchError(error => of(fromClosures.loadClosureLookupsFail({ error })))))
  ));

  clearActiveOnSearchResult$ = createEffect(() => this.actions$.pipe(
    ofType(fromSearch.loadSearchResultsSuccess),
    map(action => fromClosures.clearClosure())
  ));

  loadResultsOnChange$ = createEffect(() => this.actions$.pipe(
    ofType(
      fromClosures.changeDiameter,
      fromClosures.changeClampRing,
      fromClosures.changeFlanged,
      fromClosures.changeDesignPressure,
      fromClosures.changeDesignPressureUnit,
      fromClosures.changeDesignTempMax,
      fromClosures.changeHingeOrientation,
      fromClosures.changeDesignCodeId,
      fromClosures.changeDesignFactorId,
      fromClosures.changeFlangeClass,
      fromClosures.changeClassRating
    ),
    map(action => fromClosures.loadClosureQueryResult())));

  resetClampRingForDiameter$ = createEffect(() => this.actions$.pipe(
    ofType(fromClosures.changeDiameter),
    withLatestFrom(this.store.select(fromSelectors.selectCurrentDiameterNumericValue)),
    filter(([action, diameter]) => diameter < 24 || diameter > 30),
    map(([action, diameter]) => fromClosures.changeClampRing({ clampRingId: undefined }))
  ));

  loadQueryResults$ = createEffect(() => this.actions$.pipe(
    ofType(fromClosures.loadClosureQueryResult),
    withLatestFrom(this.store.select(fromSelectors.selectFilterQuerystring)),
    switchMap(([action, qs]) => this.closureService.getClosureQueryResults(qs)
      .pipe(map(res => fromClosures.loadClosureQueryResultSuccess({ res })),
        catchError(error => of(fromClosures.loadClosureQueryResultFailure({ error })))))));

  loadClosureDetail$ = createEffect(() => this.actions$.pipe(
    ofType(fromClosures.activateClosure),
    withLatestFrom(this.store.select(fromSelectors.selectActiveClosureId)),
    switchMap(([action, id]) => this.closureService.getClosureDetail(id || -1)
      .pipe(map(detail => fromClosures.loadClosureDetailSuccess({ detail })),
        catchError(error => of(fromClosures.loadClosureDetailFail({ error })))))));

  activateClosureByPartNumber$ = createEffect(() => this.actions$.pipe(
    ofType(fromClosures.activateClosureByPartNumber),
    switchMap((action) => this.closureService.getClosureByPartNumber(action.partNumber)
      .pipe(map(res => res 
                        ? fromClosures.activateClosure({ id: res?.id ?? -1}) 
                        : fromClosures.loadClosureDetailFail({ error: { name: 'General Error', message: `Unable to find closure with part number ${action.partNumber}`}})
        ),
        catchError(error => of(fromClosures.loadClosureDetailFail({ error })))
    ))));

  downloadClosureDrawingFromDatabase$ = createEffect(() => this.actions$.pipe(
    ofType(fromClosures.loadClosureDrawing),
    withLatestFrom(this.store.select(fromStore.selectors.USERPROFILE.selectAppConfigValue('EnableClosureFlatterFilesIntegration'))),
    filter(([action, ffEnabled]) => !ffEnabled),
    switchMap(([action, ffEnabled]) => this.closureService.getClosureDrawing(action.detail.id)
      .pipe(map(file => {
        window.open(URL.createObjectURL(file));
        return fromClosures.loadClosureDrawingSuccess();
      }))),
    catchError(error => of(fromClosures.loadClosureDrawingFail({ error })))
  ));

  downloadClosureDrawingFromFlatterFiles$ = createEffect(() => this.actions$.pipe(
    ofType(fromClosures.loadClosureDrawing),
    withLatestFrom(
      this.store.select(fromStore.selectors.USERPROFILE.selectAppConfigValue('EnableClosureFlatterFilesIntegration')),
      this.store.select(fromStore.selectors.CLOSURES.selectDrawingDictionary)
    ),
    filter(([action, ffEnabled, dwgs]) => !!ffEnabled),
    map(([action, ffEnabled, dwgs]) => action.detail.drawings.find((v, i, a) => i === 0)),
    filter(filename => !!filename?.filename),
    switchMap((filename) => this.closureService.getFlatterFilesDrawing(filename?.filename || '')
      .pipe(map(file => {
        window.open(window.URL.createObjectURL(file));
        return fromStore.actions.CLOSURES.loadClosureDrawingSuccess();
      }))),
    catchError(error => of(fromStore.actions.FITTINGS.loadDrawingFromFlatterFilesFail({ error })))
  ));

  downloadAdditionalDwg$ = createEffect(() => this.actions$.pipe(
    ofType(fromClosures.loadDrawingFromFlatterFiles),
    switchMap(action => this.closureService.getFlatterFilesDrawing(action.filename)
      .pipe(map(file => {
        window.open(window.URL.createObjectURL(file));
        return fromStore.actions.CLOSURES.loadClosureDrawingSuccess();
      }))
    )
  ));


  monitorErrors$ = createEffect(() => this.actions$.pipe(
    ofType(
      fromClosures.loadClosureDetailFail,
      fromClosures.loadClosureDrawingFail,
      fromClosures.loadClosureLookupsFail,
      fromClosures.loadClosureQueryResultFailure,),
    tap(error => this.monitoringService.logException(error.error)),
    map(error => fromErrors.addGlobalError({ error: error.error }))
  ));


  loadDwgGraph$ = createEffect(() => this.actions$.pipe(
    ofType(fromClosures.activateClosure, fromClosures.loadClosureDetailSuccess, fromClosures.loadActiveClosureDrawingDictionary),
    withLatestFrom(this.store.select(fromStore.selectors.CLOSURES.selectActiveClosurePartNumber)),
    filter(([action, partNumber]) => !!partNumber),
    switchMap(([action, partNumber]) => this.fittingsService.getFlatterFilesDrawingDictionary(partNumber || 'foo')
      .pipe(map(dictionary => fromStore.actions.CLOSURES.loadActiveClosureDrawingDictionarySuccess({ dictionary })),
        catchError(error => of(fromStore.actions.CLOSURES.loadActiveClosureDrawingDictionaryFail({ error })))))));
}
